// The opening book as one game reads it. The book comes in two files chosen by
// a game's first move, so a `GameBook` holds the file covering the game and,
// for the start position only, the moves of every file merged. The review
// session loads it once per game; every tab asks it rather than loading its
// own copy.
import { type Fen, type San } from '@chess/api-model';
import { epdOf, type BookLine, type BookReading, type Opening, type OpeningBook, type OpeningContinuation, type Openings } from '@chess/api-openings';

/** A first move whose file covers every other first move. */
const FILE_KEYS = ['e4', 'd4'] as const;

/** "5." or "5…", for the side to move of a position. */
export function movePrefixOf(fen: Fen): string {
  const fields = fen.split(' ');
  const number = fields[5] ?? '1';
  return fields[1] === 'b' ? `${number}…` : `${number}.`;
}

/**
 * The line of the game's variation: of the book's lines for the name the
 * game was given (one per position the name is given to), the one the game
 * followed furthest, i.e. whose latest position the game also reached; then
 * the longest, then the first in the book. Undefined when no line meets
 * the game at all.
 */
export function variationLine(lines: readonly BookLine[], positions: readonly Fen[]): BookLine | undefined {
  const reached = new Set(positions.map(epdOf));
  let chosen: { line: BookLine; followed: number } | undefined;
  for (const line of lines) {
    let followed = -1;
    line.positions.forEach((fen, i) => {
      if (reached.has(epdOf(fen))) followed = i;
    });
    if (followed < 0) continue;
    if (!chosen || followed > chosen.followed || (followed === chosen.followed && line.moves.length > chosen.line.moves.length)) chosen = { line, followed };
  }
  return chosen?.line;
}

/** The variation line's move from a position on it; undefined off the line or at its end. */
export function lineMoveFrom(line: BookLine | undefined, fen: Fen): San | undefined {
  if (!line) return undefined;
  const key = epdOf(fen);
  const at = line.positions.findIndex((p) => epdOf(p) === key);
  return at >= 0 ? line.moves[at] : undefined;
}

export class GameBook {
  /** The file covering the game. */
  readonly book: OpeningBook;
  /** What the whole game was playing, when the book names any of its positions. */
  readonly reading?: BookReading;
  /** The line of the variation the game is named after, as the book has it (`variationLine`). */
  readonly variation?: BookLine;
  /** Every file, for the start position's merged first moves; empty until loaded. */
  private readonly files: OpeningBook[];

  private constructor(book: OpeningBook, reading: BookReading | undefined, files: OpeningBook[], variation?: BookLine) {
    this.book = book;
    this.reading = reading;
    this.files = files;
    this.variation = variation;
  }

  /** The book for a game, from its first move, with the start position's files loaded too. */
  static async load(openings: Openings, game: { moves: { san: string }[]; positions: Fen[] }): Promise<GameBook> {
    const first = game.moves[0]?.san ?? 'e4';
    const [book, ...files] = await Promise.all([openings.load(first as never), ...FILE_KEYS.map((k) => openings.load(k as never).catch(() => undefined))]);
    const reading = book.book(game.positions);
    const variation = reading ? variationLine(book.lines(reading.opening.name), game.positions) : undefined;
    return new GameBook(book, reading, files.filter((f): f is OpeningBook => !!f), variation);
  }

  /** The variation's next move from a position on its line, the main line's while the two share their moves; undefined off it or past its end. */
  variationMoveFrom(fen: Fen): San | undefined {
    return lineMoveFrom(this.variation, fen);
  }

  /** The opening this exact position is, if it is one. */
  lookup(fen: Fen): Opening | undefined {
    return this.book.lookup(fen);
  }

  /** The book has a name or a move for this position. */
  knows(fen: Fen): boolean {
    return this.book.knows(fen);
  }

  /** The book's moves from a position; at the start, every file's first moves merged. */
  movesFrom(fen: Fen, atStart = false): OpeningContinuation[] {
    if (!atStart) return this.book.continuations(fen);
    const seen = new Set<string>();
    const merged: OpeningContinuation[] = [];
    for (const file of this.files.length ? this.files : [this.book]) {
      for (const c of file.continuations(fen)) {
        if (seen.has(c.san)) continue;
        seen.add(c.san);
        merged.push(c);
      }
    }
    return merged;
  }
}
